import { useState, useRef, useCallback } from 'react';
import { ChevronRightIcon } from '@/components/ui/Icons';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import type { ReadyMonthSeparatorDisplay } from '@/hooks/useReadyMonthSeparatorDisplay';
import { Switch } from '@/components/ui/Switch/Switch';
import './TableContextMenu.css';
import './ReadyMonthSeparatorContextMenu.css';

interface ReadyMonthSeparatorContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorPosition: { x: number; y: number } | null;
  display: ReadyMonthSeparatorDisplay;
  setShowReadyTime: (value: boolean) => void;
  setShowInProgress: (value: boolean) => void;
  setShowProjectCount: (value: boolean) => void;
  setShowActiveProjectsCount: (value: boolean) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}

export function ReadyMonthSeparatorContextMenu({
  isOpen,
  onClose,
  anchorPosition,
  display,
  setShowReadyTime,
  setShowInProgress,
  setShowProjectCount,
  setShowActiveProjectsCount,
  onExpandAll,
  onCollapseAll,
}: ReadyMonthSeparatorContextMenuProps) {
  const [displaySubAnchor, setDisplaySubAnchor] = useState<HTMLElement | null>(null);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelSubmenuClose = useCallback(() => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleSubmenuClose = useCallback(() => {
    submenuCloseTimerRef.current = setTimeout(() => {
      setDisplaySubAnchor(null);
      submenuCloseTimerRef.current = null;
    }, 150);
  }, []);

  return (
    <ContextMenu isOpen={isOpen} onClose={onClose} anchorPosition={anchorPosition}>
      <ul className="context-menu__list table-context-menu__list ready-month-sep-context-menu">
        <li
          className="context-menu__list-item table-context-menu__item_with-sub"
          onMouseEnter={(e) => {
            cancelSubmenuClose();
            setDisplaySubAnchor(e.currentTarget);
          }}
          onMouseLeave={() => scheduleSubmenuClose()}
        >
          <button type="button" className="context-menu__action context-menu__action_with-sub">
            <span className="context-menu__action-text">Настройки отображения</span>
            <ChevronRightIcon size={16} className="context-menu__action-chevron" />
          </button>
          {displaySubAnchor && (
            <ContextMenuSubmenu
              anchorEl={displaySubAnchor}
              onMouseEnter={cancelSubmenuClose}
              onMouseLeave={() => setDisplaySubAnchor(null)}
            >
              <ul className="context-menu__list ready-month-sep-context-menu">
                <li className="context-menu__list-item">
                  <Switch
                    className="context-menu__label"
                    checked={display.showProjectCount}
                    onCheckedChange={() => setShowProjectCount(!display.showProjectCount)}
                    onClick={(e) => e.stopPropagation()}
                    label={<span className="context-menu__label-text">Готовые проекты</span>}
                  />
                </li>
                <li className="context-menu__list-item">
                  <Switch
                    className="context-menu__label"
                    checked={display.showReadyTime}
                    onCheckedChange={() => setShowReadyTime(!display.showReadyTime)}
                    onClick={(e) => e.stopPropagation()}
                    label={<span className="context-menu__label-text">Готовое время</span>}
                  />
                </li>
                <li className="context-menu__list-item">
                  <Switch
                    className="context-menu__label"
                    checked={display.showActiveProjectsCount}
                    onCheckedChange={() => setShowActiveProjectsCount(!display.showActiveProjectsCount)}
                    onClick={(e) => e.stopPropagation()}
                    label={<span className="context-menu__label-text">Проекты в процессе</span>}
                  />
                </li>
                <li className="context-menu__list-item">
                  <Switch
                    className="context-menu__label"
                    checked={display.showInProgress}
                    onCheckedChange={() => setShowInProgress(!display.showInProgress)}
                    onClick={(e) => e.stopPropagation()}
                    label={<span className="context-menu__label-text">Время в процессе</span>}
                  />
                </li>
              </ul>
            </ContextMenuSubmenu>
          )}
        </li>
        <li className="context-menu__list-item">
          <button
            type="button"
            className="context-menu__action"
            onClick={() => {
              onExpandAll();
              onClose();
            }}
          >
            <span className="context-menu__action-text">Раскрыть всё</span>
          </button>
        </li>
        <li className="context-menu__list-item">
          <button
            type="button"
            className="context-menu__action"
            onClick={() => {
              onCollapseAll();
              onClose();
            }}
          >
            <span className="context-menu__action-text">Свернуть всё</span>
          </button>
        </li>
      </ul>
    </ContextMenu>
  );
}
